import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import * as cciApi from '@di-shared/api/cci/auth';
import {diApi} from '@di-shared/api/di';
import {ApiResponseGetCciUbiResponse, ApiResponseGetCpoGradeResponse} from '@di-shared/api/di/__generated';
import {DetailGrade, Grade} from '@di-shared/api/di/type';
import {DEFAULT_CACHE_TIME, ERROR, UNIT} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import Popper from '@di-shared/features/common/Popper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import MyMaintenanceGrade from '@di-shared/features/diag/cpo/MyMaintenanceGrade';
import MyMaintenanceGradeError from '@di-shared/features/diag/cpo/MyMaintenanceGradeError';
import MyMaintenanceGradeLoading from '@di-shared/features/diag/cpo/MyMaintenanceGradeLoading';
import environmentStore from '@di-shared/features/environment/environmentStore';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface DiagCpoContentsProps {
  vin: string;
}

function DiagCpoContents({vin}: DiagCpoContentsProps) {
  const {t} = useInferableTranslation();
  const puid = environmentStore.getState().puid;
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  const [{data: meData}, {data: myCarDetailData}, {data: ubiData}, {data: cpoData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(cciApi.getMe)],
        queryFn: () => cciApi.getMe(),
        ...DEFAULT_CACHE_TIME,
      },
      {
        queryKey: [getQueryKey(cciApi.getMyCarDetail), vin],
        queryFn: () => cciApi.getMyCarDetail(vin),
        ...DEFAULT_CACHE_TIME,
      },
      {
        queryKey: [getQueryKey(diApi.ubiController.getUbi4), vin],
        queryFn: () => diApi.ubiController.getUbi4({vin}),
        select: ({data}: ApiResponseGetCciUbiResponse) => ({
          ...data,
          scoreDate: parseDateWithFormat(data.scoreDate),
        }),
      },
      {
        queryKey: [getQueryKey(diApi.cpo.getCpoGrade), vin, puid],
        queryFn: () => diApi.cpo.getCpoGrade({vin, puid}),
        select: ({data}: ApiResponseGetCpoGradeResponse) => ({
          ...data,
          agreeDate: parseDateWithFormat(data.agreeDate),
        }),
      },
    ],
  });

  function SafeDrivingMessage({grade}: {grade: DetailGrade}) {
    if (grade === DetailGrade.S) {
      return (
        <strong
          className="certification_driving_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T20233_$carName', {carName: myCarDetailData.vehicleModelName})}
        </strong>
      );
    } else if (grade === DetailGrade['A+']) {
      return (
        <strong
          className="certification_driving_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T20235')}
        </strong>
      );
    } else if (grade === DetailGrade.A) {
      return (
        <strong
          className="certification_driving_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T20237')}
        </strong>
      );
    } else if (grade === DetailGrade['A-']) {
      return (
        <strong
          className="certification_driving_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T20239')}
        </strong>
      );
    } else if (grade === DetailGrade.B) {
      return (
        <strong
          className="certification_driving_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T20240')}
        </strong>
      );
    } else {
      return (
        <strong
          className="certification_driving_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T20241_$distance', {
            distance: `${convertDistanceValue(1000 - ubiData.rangeDrvDist)}${convertDistanceSymbol(1000 - ubiData.rangeDrvDist)}`,
          })}
        </strong>
      );
    }
  }

  return (
    <main
      id="content"
      className="content page_car_management"
    >
      <section className="section section_car_certification">
        <section className="section section_certification_comprehensive">
          <div className="section_container">
            <div className="section_container_header">
              <h2
                className="title_medium_b"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T20217_$name_$carName', {
                  name: meData.name,
                  carName: myCarDetailData.vehicleNameView,
                })}
              </h2>
              {cpoData.totalGrade === Grade.S || cpoData.totalGrade === Grade.A || cpoData.totalGrade === Grade.B ? (
                <div className="my_comprehensive_grade">
                  <span
                    className={clsx('ic ic_certification_grade', {
                      grade_s: cpoData.totalGrade === Grade.S,
                      grade_a: cpoData.totalGrade === Grade.A,
                      grade_b: cpoData.totalGrade === Grade.B,
                    })}
                  >
                    <span className="blind">{t('T20218_$grade', {grade: cpoData.totalGrade})}</span>
                  </span>
                </div>
              ) : cpoData.totalGrade === Grade.C ? (
                <div className="badge_box">
                  <span className="badge badge_25px badge_filled_surface_container_lowest">{t('T20047')}</span>
                </div>
              ) : (
                <div className="badge_box">
                  <span className="badge badge_25px badge_filled_surface_container_lowest">{t('T20067')}</span>
                </div>
              )}
            </div>
            <span className="section_container_caption">
              {t('T20219_$agreeDate', {
                agreeDate: ubiData.scoreDate ? CldrDatetimeFormatter.format(ubiData.scoreDate, 'yMMMaHM') : ERROR.DASH,
              })}
            </span>
            {/* [D] comprehensive_grade에 혜택 미대상일 경우 type_null 클래스 추가, 에러 케이스일 경우 type_error 클래스 추가 */}
            <div
              className={clsx('comprehensive_grade', {
                type_null: cpoData.totalGrade === Grade.C,
                type_error: cpoData.totalGrade === Grade.E,
              })}
            >
              {/* FIXME : 실제 API에서 어떻게 분기처리할지 다시 봐야함 (종합등급 = 차량인증 + 안전운전?) */}
              <>
                <span className="comprehensive_grade_item grade_etc">
                  {cpoData.totalGrade === Grade.E ? (
                    <span className="speech_bubble">{t('T20278')}</span>
                  ) : cpoData.safeDrivingGrade === DetailGrade.C && cpoData.maintenanceGrade === Grade.C ? (
                    <span className="speech_bubble">{t('T20279')}</span>
                  ) : cpoData.safeDrivingGrade === DetailGrade.C ? (
                    <span className="speech_bubble">{t('T20280')}</span>
                  ) : (
                    cpoData.maintenanceGrade === Grade.C && <span className="speech_bubble">{t('T20281')}</span>
                  )}
                </span>
              </>
              <span
                className="comprehensive_grade_item grade_b"
                aria-current={cpoData.totalGrade === Grade.B}
              >
                {cpoData.totalGrade !== Grade.C && cpoData.totalGrade !== Grade.E && <span className="grade_text">B</span>}
                <span className="speech_bubble">{t('T20222')}</span>
              </span>
              <span
                className="comprehensive_grade_item grade_a"
                aria-current={cpoData.totalGrade === Grade.A}
              >
                {cpoData.totalGrade !== Grade.C && cpoData.totalGrade !== Grade.E && <span className="grade_text">A</span>}
                <span className="speech_bubble">{t('T20221')}</span>
              </span>
              <span
                className="comprehensive_grade_item grade_s"
                aria-current={cpoData.totalGrade === Grade.S}
              >
                {cpoData.totalGrade !== Grade.C && cpoData.totalGrade !== Grade.E && <span className="grade_text">S</span>}
                <span className="speech_bubble">{t('T20220')}</span>
              </span>
            </div>

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('T20225')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">
                    {cpoData.maintenanceGrade === Grade.E
                      ? t('T20067')
                      : cpoData.maintenanceGrade === Grade.C
                        ? t('T20080')
                        : t('T20218_$grade', {grade: cpoData.maintenanceGrade})}
                  </span>
                  <span className="statistics_info_unit"></span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('T20226')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">
                    {cpoData.safeDrivingGrade === DetailGrade.E
                      ? t('T20067')
                      : cpoData.safeDrivingGrade === DetailGrade.C
                        ? t('T20227')
                        : t('T20218_$grade', {grade: cpoData.safeDrivingGrade})}
                  </span>
                  <span className="statistics_info_unit"></span>
                </span>
              </li>
            </ul>
          </div>

          <div className="driving_banner type_square">
            <LinkTo
              to={ROUTE_PATHS.CPO_GUIDE.getFullPath()}
              className={'driving_banner_box'}
              onClick={() => {
                emitEventLog({
                  event: 'di_vehicle_management',
                  data_action: 'view banner',
                  data_name: '등급은 어떻게 산정될까?',
                });
              }}
            >
              <span className="driving_banner_txtbox type_reverse">
                <strong className="driving_banner_title body_large_b">{t('T20229')}</strong>
                <span className="driving_banner_desc body_small_r">{t('T20228')}</span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_guide.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_guide.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_guide.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_guide.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_certification_guide.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_certification_guide.png')}
                />
              </span>
            </LinkTo>
          </div>
        </section>
        <ErrorBoundary fallback={<MyMaintenanceGradeError />}>
          <Suspense fallback={<MyMaintenanceGradeLoading />}>
            <MyMaintenanceGrade vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <section className="section section_certification_driving">
          <div className="section_container_header">
            <h3 className="title_medium_b">{t('T20226')}</h3>
            <div className="my_grade">
              {cpoData.safeDrivingGrade === DetailGrade.S ||
              cpoData.safeDrivingGrade === DetailGrade['A+'] ||
              cpoData.safeDrivingGrade === DetailGrade.A ||
              cpoData.safeDrivingGrade === DetailGrade['A-'] ||
              cpoData.safeDrivingGrade === DetailGrade.B ? (
                <span
                  className={clsx('ic ic_certification_grade', {
                    grade_s: cpoData.safeDrivingGrade === DetailGrade.S,
                    grade_a:
                      cpoData.safeDrivingGrade === DetailGrade.A ||
                      cpoData.safeDrivingGrade === DetailGrade['A+'] ||
                      cpoData.safeDrivingGrade === DetailGrade['A-'],
                    grade_b: cpoData.safeDrivingGrade === DetailGrade.B,
                  })}
                >
                  <span className="blind">{t('T20218_$grade', {grade: cpoData.safeDrivingGrade})}</span>
                </span>
              ) : cpoData.safeDrivingGrade === DetailGrade.C ? (
                <span className="my_grade_text">{t('T10135')}</span>
              ) : (
                <span className="my_grade_text">{t('T20067')}</span>
              )}
            </div>
          </div>
          <div className="section_container">
            <div className="certification_driving_head">
              <SafeDrivingMessage grade={cpoData.safeDrivingGrade as DetailGrade} />

              <Popper
                placement="bottom"
                className="tooltip"
                tooltip={
                  <div
                    tabIndex={0}
                    role="tooltip"
                  >
                    <strong className="tooltip_title">{t('T20242')}</strong>
                    <ul className="tooltip_list">
                      <li className="tooltip_item">{t('T20243')}</li>
                    </ul>
                    <div className="tooltip_table">
                      <table>
                        <caption>
                          <span className="blind">{t('T20244')}</span>
                        </caption>
                        <colgroup>
                          <col />
                          <col />
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col">{t('T20245')}</th>
                            <th scope="col">{t('T20246')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <th scope="row">{t('T20247')}</th>
                            <td>{t('T20220')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('T20248')}</th>
                            <td>{t('T20221')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('T20249')}</th>
                            <td>{t('T20222')}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t('T10135')}</th>
                            <td>{t('T20227')}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                }
              >
                <div className="tooltip_area tooltip_area_single">
                  <button
                    type="button"
                    className="btn_tooltip"
                    aria-describedby="tooltipBox01"
                    aria-controls="tooltipStatement01"
                    aria-expanded="false"
                  >
                    <span
                      className="ic ic_information_16px"
                      aria-hidden="true"
                    ></span>
                    <span className="blind">{t('T10044')}</span>
                  </button>
                </div>
              </Popper>
            </div>
            {cpoData.safeDrivingGrade === DetailGrade.C ? (
              <div className="certification_distance">
                <div className="certification_distance_info">
                  <span className="certification_distance_title">{t('T20022')}</span>
                  <div className="certification_distance_desc">
                    <span className="certification_distance_value">{convertDistanceValue(ubiData.rangeDrvDist)}</span>
                    <span className="certification_distance_unit">{convertDistanceSymbol(ubiData.rangeDrvDist)}</span>

                    <div className="badge_box">
                      <span className="badge badge_25px badge_filled_surface_container_lowest">{t('T20230')}</span>
                    </div>
                  </div>
                </div>
                <div className="certification_distance_progress">
                  <div className="slider slider_progress">
                    <label
                      htmlFor="distanceProgress"
                      className="blind"
                    >
                      {t('T10141')}
                    </label>
                    <div className="slider_box">
                      <progress
                        id="distanceProgress"
                        className="slider_bar"
                        max="1000"
                        value={ubiData.rangeDrvDist}
                      ></progress>
                    </div>
                  </div>
                  <div className="progress_text">
                    <span className="progress_value">
                      <span className="blind">{t('T10142')}</span>
                      {convertDistanceValue(ubiData.rangeDrvDist)}
                      {convertDistanceSymbol(ubiData.rangeDrvDist)}
                    </span>
                    <span className="progress_max">
                      <span className="blind">{t('T10143')}</span>
                      1,000km
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <ul className="certification_info_list">
                <li className="certification_info_item">
                  <span className="certification_info_title">{myCarDetailData.vehicleModelName}</span>
                  <span className="certification_info_value">
                    <span className="certification_info_text"> {t('T20053')}</span>
                    <span className="certification_info_number">
                      {/* FIXME: getUbiDistributionPercentText 수정 후 반영 */}
                      {ubiData.modelDistribution}
                      {UNIT.PERCENT}
                    </span>
                  </span>

                  <div className="badge_box">
                    <span className="badge badge_basic_container badge_20px">{t('T20231_$score', {score: ubiData.safetyDrvScore})}</span>
                  </div>
                </li>
                <li className="certification_info_item">
                  <span className="certification_info_title">{t('T20022')}</span>
                  <span className="certification_info_value">
                    <span className="certification_info_number">{convertDistanceValue(ubiData.rangeDrvDist)}</span>
                    <span className="certification_info_unit">{convertDistanceSymbol(ubiData.rangeDrvDist)}</span>
                  </span>

                  <div className="badge_box">
                    <span className="badge badge_basic_container badge_20px">{t('T20232')}</span>
                  </div>
                </li>
              </ul>
            )}
          </div>

          <div className="driving_banner type_square">
            <LinkTo
              to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
              className="driving_banner_box"
              onClickBypass={() => {
                emitEventLog({
                  event: 'di_vehicle_management',
                  data_action: 'view banner',
                  data_name: '나의 안전운전 점수 상세 보기',
                });
              }}
            >
              <span className="driving_banner_txtbox type_reverse">
                <strong className="driving_banner_title body_large_b">{t('T30256')}</strong>
                <span className="driving_banner_desc body_small_r">{t('T30257')}</span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_safe_score.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_safe_score.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_safe_score.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_certification_safe_score.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_certification_safe_score.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_certification_safe_score.png')}
                />
              </span>
            </LinkTo>
          </div>
        </section>
      </section>
    </main>
  );
}

export default DiagCpoContents;
